/** Rubrique Corbeille (Cave) : un seul écran listant les cuvées et domaines archivés, avec
 * restauration en un clic (désarchivage) et suppression définitive. */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { RotateCcw, Trash2, Wine, MapPin } from 'lucide-react';
import { caveApi, vignoblesApi } from '../../../services/api';
import { Spinner, EmptyState, Button, Card } from '../../../components/ui/kit';
import { WineColorDot } from '../../../components/wine/wine';

export default function Corbeille() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['archives'], queryFn: caveApi.archives });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['archives'] });
    qc.invalidateQueries({ queryKey: ['stock'] });
    qc.invalidateQueries({ queryKey: ['domains'] });
  };

  const restoreCuvee = async (c) => { await caveApi.archiveCuvee(c.id, false); toast.success(`« ${c.name} » restaurée`); refresh(); };
  const deleteCuvee = async (c) => { if (!window.confirm(`Supprimer définitivement « ${c.name} » ?`)) return; await caveApi.deleteCuvee(c.id); toast.success('Cuvée supprimée'); refresh(); };
  const restoreDomain = async (d) => { await vignoblesApi.archiveDomain(d.id, false); toast.success(`« ${d.name} » restauré`); refresh(); };
  const deleteDomain = async (d) => {
    if (!window.confirm(`Supprimer définitivement « ${d.name} » ?`)) return;
    try { await vignoblesApi.deleteDomain(d.id); toast.success('Domaine supprimé'); refresh(); }
    catch (e) { toast.error(e.message || 'Suppression impossible (cuvées rattachées)'); }
  };

  if (isLoading) return <Spinner />;
  const cuvees = data?.cuvees || [];
  const domains = data?.domains || [];
  const empty = cuvees.length === 0 && domains.length === 0;

  return (
    <div className="max-w-[640px]">
      <h2 className="font-title text-3xl mb-1">Corbeille</h2>
      <p className="text-[13px] text-n600 mb-4">Cuvées et domaines archivés. Restaurez-les ou supprimez-les définitivement.</p>

      {empty ? (
        <EmptyState title="Corbeille vide" description="Les cuvées et domaines archivés apparaîtront ici." testId="corbeille-empty" />
      ) : (
        <div className="space-y-6">
          {cuvees.length > 0 && (
            <section data-testid="corbeille-cuvees">
              <p className="kicker mb-2">Cuvées archivées ({cuvees.length})</p>
              <Card className="divide-y divide-hair">
                {cuvees.map((c) => (
                  <div key={c.id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`corbeille-cuvee-${c.id}`}>
                    <Wine className="w-4 h-4 text-n500 shrink-0" strokeWidth={1.6} />
                    <WineColorDot color={c.color} />
                    <div className="flex-1 min-w-0">
                      <p className="text-[14px] truncate">{c.name}</p>
                      <p className="text-[12px] text-n500">{c.domain_name} · {c.stock} réf.</p>
                    </div>
                    <Button className="h-8 text-[12px]" onClick={() => restoreCuvee(c)} data-testid={`restore-cuvee-${c.id}`}><RotateCcw className="w-3.5 h-3.5" /> Restaurer</Button>
                    <button className="text-n500 hover:text-red-600" title="Supprimer définitivement" onClick={() => deleteCuvee(c)} data-testid={`purge-cuvee-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </Card>
            </section>
          )}
          {domains.length > 0 && (
            <section data-testid="corbeille-domains">
              <p className="kicker mb-2">Domaines archivés ({domains.length})</p>
              <Card className="divide-y divide-hair">
                {domains.map((d) => (
                  <div key={d.id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`corbeille-domain-${d.id}`}>
                    <MapPin className="w-4 h-4 text-n500 shrink-0" strokeWidth={1.6} />
                    <div className="flex-1 min-w-0">
                      <p className="text-[14px] truncate">{d.name}</p>
                      <p className="text-[12px] text-n500">{d.region || '—'} · {d.cuvee_count} cuvée(s)</p>
                    </div>
                    <Button className="h-8 text-[12px]" onClick={() => restoreDomain(d)} data-testid={`restore-domain-${d.id}`}><RotateCcw className="w-3.5 h-3.5" /> Restaurer</Button>
                    <button className="text-n500 hover:text-red-600" title="Supprimer définitivement" onClick={() => deleteDomain(d)} data-testid={`purge-domain-${d.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </Card>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
